import { FormEvent, useState } from 'react';
import type {
  BatteryPowerSource,
  CatalogRecord,
  CatalogStatus,
  CatalogVariable,
  CatalogWireless,
  CatalogWritePayload,
  FirmwareUpdateChannel,
  VariableAvailability,
  VariableDataType,
  VariableSource,
  WirelessModule,
  WirelessProtocol,
} from '../types/registry';
import { catalogHelp } from '../lib/fieldHelpCopy';
import { FieldHelp, FieldHelpGroup, SectionHelp } from './FieldHelp';

const STATUSES: CatalogStatus[] = ['ACTIVE', 'DEPRECATED', 'DISABLED'];

const UPDATE_CHANNELS: FirmwareUpdateChannel[] = ['stable', 'beta', 'legacy'];

const POWER_SOURCES: BatteryPowerSource[] = ['battery', 'mains', 'solar', 'hybrid'];

const WIRELESS_MODULES: WirelessModule[] = ['wifi', 'ble', 'lora', 'cellular'];

const WIRELESS_PROTOCOLS: WirelessProtocol[] = ['mqtt', 'lorawan'];

const MQTT_MODULES: ReadonlySet<WirelessModule> = new Set(['wifi', 'ble', 'cellular']);

const VARIABLE_SOURCES: VariableSource[] = ['raw', 'derived', 'external_sensor', 'computed'];

const VARIABLE_DATA_TYPES: VariableDataType[] = ['number', 'boolean', 'string', 'enum'];

const VARIABLE_AVAILABILITY: Array<VariableAvailability | ''> = [
  '',
  'always',
  'optional',
  'external_sensor',
];

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function joinCsv(values: string[] | undefined): string {
  return (values ?? []).join(', ');
}

function emptyVariable(): CatalogVariable {
  return {
    key: '',
    label: '',
    unit: '',
    ingestion_keys: [],
    source: 'raw',
    data_type: 'number',
    required: true,
  };
}

function normalizeWireless(seed?: CatalogWireless): CatalogWireless {
  const modules = (seed?.modules ?? []).filter((m): m is WirelessModule =>
    WIRELESS_MODULES.includes(m as WirelessModule),
  );
  const protocols = (seed?.protocols ?? []).filter((p): p is WirelessProtocol =>
    WIRELESS_PROTOCOLS.includes(p as WirelessProtocol),
  );
  return { modules: [...modules], protocols: [...protocols] };
}

function validateWireless(wireless: CatalogWireless): string | null {
  const moduleSet = new Set(wireless.modules);
  const protocolSet = new Set(wireless.protocols);
  if ([...MQTT_MODULES].some((m) => moduleSet.has(m)) && !protocolSet.has('mqtt')) {
    return 'MQTT is required when Wi‑Fi, BLE, or Cellular is selected';
  }
  if (moduleSet.has('lora') && !protocolSet.has('lorawan')) {
    return 'LoRaWAN is required when LoRa is selected';
  }
  return null;
}

export function defaultCatalogForm(seed?: Partial<CatalogRecord>): CatalogWritePayload {
  const typeName = seed?.type_name ?? '';
  return {
    type_id: seed?.type_id,
    type_name: typeName,
    type_aliases: Array.isArray(seed?.type_aliases) ? [...seed.type_aliases] : [],
    description: seed?.description ?? '',
    manufacturer: seed?.manufacturer ?? '',
    model: seed?.model ?? '',
    hardware_version: seed?.hardware_version ?? '1.0',
    category: seed?.category ?? '',
    status: (STATUSES.includes(seed?.status as CatalogStatus)
      ? seed?.status
      : 'ACTIVE') as CatalogStatus,
    firmware: {
      supported_versions: seed?.firmware?.supported_versions ?? [],
      minimum_version: seed?.firmware?.minimum_version ?? '',
      latest_version: seed?.firmware?.latest_version ?? '',
      update_channel: seed?.firmware?.update_channel,
    },
    display: {
      has_display: seed?.display?.has_display ?? false,
      display_reference: seed?.display?.display_reference ?? '',
      has_color: seed?.display?.has_color ?? false,
    },
    wireless: normalizeWireless(seed?.wireless),
    features: {
      allows_external_sensor: seed?.features?.allows_external_sensor ?? false,
      supports_battery: seed?.features?.supports_battery ?? false,
      supports_alerts: seed?.features?.supports_alerts ?? false,
      supports_history: seed?.features?.supports_history ?? true,
      supports_location: seed?.features?.supports_location ?? false,
      supports_calibration: seed?.features?.supports_calibration ?? false,
      supports_fwota: seed?.features?.supports_fwota ?? false,
    },
    battery: {
      power_source: seed?.battery?.power_source ?? 'battery',
      variable_key: seed?.battery?.variable_key ?? '',
      unit: seed?.battery?.unit ?? '',
      reference: seed?.battery?.reference ?? '',
      full_value: seed?.battery?.full_value,
      low_threshold: seed?.battery?.low_threshold,
      critical_threshold: seed?.battery?.critical_threshold,
      expected_lifetime_days: seed?.battery?.expected_lifetime_days,
      replaceable: seed?.battery?.replaceable ?? false,
    },
    external_sensor: {
      allowed_types: seed?.external_sensor?.allowed_types ?? [],
      max_count: seed?.external_sensor?.max_count,
      required: seed?.external_sensor?.required ?? false,
      variables: seed?.external_sensor?.variables ?? [],
    },
    telemetry: {
      expected_interval_seconds: seed?.telemetry?.expected_interval_seconds ?? 300,
      offline_after_seconds: seed?.telemetry?.offline_after_seconds ?? 900,
      timestamp_unit: 'epoch_ms',
    },
    variables: seed?.variables?.length
      ? seed.variables.map((v) => ({
          ...emptyVariable(),
          ...v,
          ingestion_keys: [...(v.ingestion_keys ?? [])],
          normal_range: v.normal_range ? { ...v.normal_range } : undefined,
          thresholds: v.thresholds ? { ...v.thresholds } : undefined,
        }))
      : [],
  };
}

/** Clone a catalog row into a create seed: new name required, aliases cleared, type_id omitted. */
export function catalogCopySeed(row: CatalogRecord): Partial<CatalogRecord> {
  const { type_id: _omit, ...rest } = row;
  return {
    ...rest,
    type_name: '',
    type_aliases: [],
  };
}

function optionalNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

function buildPayload(form: CatalogWritePayload, aliasesCsv: string, fwVersionsCsv: string, extTypesCsv: string, extVarsCsv: string): CatalogWritePayload {
  const typeName = form.type_name.trim();
  const aliases = splitCsv(aliasesCsv);
  const firmwareVersions = splitCsv(fwVersionsCsv);

  const variables: CatalogVariable[] = form.variables
    .map((v) => {
      const item: CatalogVariable = {
        key: v.key.trim(),
        label: v.label.trim(),
        unit: v.unit.trim(),
        ingestion_keys: v.ingestion_keys.map((k) => k.trim()).filter(Boolean),
        source: v.source,
        data_type: v.data_type,
        required: v.required,
      };
      if (v.availability) item.availability = v.availability;
      if (
        v.normal_range &&
        Number.isFinite(v.normal_range.min) &&
        Number.isFinite(v.normal_range.max)
      ) {
        item.normal_range = { min: v.normal_range.min, max: v.normal_range.max };
      }
      if (
        v.thresholds &&
        Number.isFinite(v.thresholds.warning_low) &&
        Number.isFinite(v.thresholds.warning_high) &&
        Number.isFinite(v.thresholds.critical_low) &&
        Number.isFinite(v.thresholds.critical_high)
      ) {
        item.thresholds = { ...v.thresholds };
      }
      return item;
    })
    .filter((v) => v.key || v.label);

  const payload: CatalogWritePayload = {
    type_name: typeName,
    type_aliases: aliases,
    hardware_version: form.hardware_version.trim() || '1.0',
    category: form.category.trim(),
    status: form.status,
    display: {
      has_display: form.display.has_display,
      ...(form.display.has_display
        ? {
            display_reference: form.display.display_reference?.trim() || undefined,
            has_color: form.display.has_color,
          }
        : {}),
    },
    wireless: {
      modules: [...form.wireless.modules],
      protocols: [...form.wireless.protocols],
    },
    features: { ...form.features },
    battery: {
      power_source: form.battery.power_source,
    },
    telemetry: {
      expected_interval_seconds: form.telemetry.expected_interval_seconds,
      offline_after_seconds: form.telemetry.offline_after_seconds,
      timestamp_unit: 'epoch_ms',
    },
    variables,
  };

  if (form.description?.trim()) payload.description = form.description.trim();
  if (form.manufacturer?.trim()) payload.manufacturer = form.manufacturer.trim();
  if (form.model?.trim()) payload.model = form.model.trim();

  const fw = form.firmware ?? {};
  const firmware: NonNullable<CatalogWritePayload['firmware']> = {};
  if (firmwareVersions.length) firmware.supported_versions = firmwareVersions;
  if (fw.minimum_version?.trim()) firmware.minimum_version = fw.minimum_version.trim();
  if (fw.latest_version?.trim()) firmware.latest_version = fw.latest_version.trim();
  if (fw.update_channel) firmware.update_channel = fw.update_channel;
  if (Object.keys(firmware).length) payload.firmware = firmware;

  const bat = form.battery;
  if (bat.variable_key?.trim()) payload.battery.variable_key = bat.variable_key.trim();
  if (bat.unit?.trim()) payload.battery.unit = bat.unit.trim();
  if (bat.reference?.trim()) payload.battery.reference = bat.reference.trim();
  if (bat.full_value !== undefined && Number.isFinite(bat.full_value)) {
    payload.battery.full_value = bat.full_value;
  }
  if (bat.low_threshold !== undefined && Number.isFinite(bat.low_threshold)) {
    payload.battery.low_threshold = bat.low_threshold;
  }
  if (bat.critical_threshold !== undefined && Number.isFinite(bat.critical_threshold)) {
    payload.battery.critical_threshold = bat.critical_threshold;
  }
  if (bat.expected_lifetime_days !== undefined && Number.isFinite(bat.expected_lifetime_days)) {
    payload.battery.expected_lifetime_days = bat.expected_lifetime_days;
  }
  if (bat.replaceable !== undefined) payload.battery.replaceable = bat.replaceable;

  if (form.features.allows_external_sensor) {
    const allowed = splitCsv(extTypesCsv);
    const vars = splitCsv(extVarsCsv);
    payload.external_sensor = {
      required: form.external_sensor?.required ?? false,
      ...(allowed.length ? { allowed_types: allowed } : {}),
      ...(vars.length ? { variables: vars } : {}),
      ...(form.external_sensor?.max_count !== undefined &&
      Number.isFinite(form.external_sensor.max_count)
        ? { max_count: form.external_sensor.max_count }
        : {}),
    };
  }

  return payload;
}

interface DeviceCatalogFormProps {
  mode: 'create' | 'edit';
  initial?: Partial<CatalogRecord>;
  /** When true (Copy flow), show hint that a new type name is required. */
  copyFrom?: boolean;
  categories: string[];
  manufacturers: string[];
  models: string[];
  submitting?: boolean;
  error?: string | null;
  onSubmit: (payload: CatalogWritePayload) => void;
  onCancel: () => void;
}

export function DeviceCatalogForm({
  mode,
  initial,
  copyFrom = false,
  categories,
  manufacturers,
  models,
  submitting = false,
  error,
  onSubmit,
  onCancel,
}: DeviceCatalogFormProps): JSX.Element {
  const [form, setForm] = useState<CatalogWritePayload>(() => defaultCatalogForm(initial));
  const [aliasesCsv, setAliasesCsv] = useState(() => joinCsv(defaultCatalogForm(initial).type_aliases));
  const [fwVersionsCsv, setFwVersionsCsv] = useState(() =>
    joinCsv(defaultCatalogForm(initial).firmware?.supported_versions),
  );
  const [extTypesCsv, setExtTypesCsv] = useState(() =>
    joinCsv(defaultCatalogForm(initial).external_sensor?.allowed_types),
  );
  const [extVarsCsv, setExtVarsCsv] = useState(() =>
    joinCsv(defaultCatalogForm(initial).external_sensor?.variables),
  );
  const initialCategory = (initial?.category ?? '').trim();
  const [customCategory, setCustomCategory] = useState(
    () => Boolean(initialCategory) && !categories.includes(initialCategory),
  );
  const initialModel = (initial?.model ?? '').trim();
  const [customModel, setCustomModel] = useState(
    () => Boolean(initialModel) && !models.includes(initialModel),
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const [step, setStep] = useState(0);

  const CATALOG_STEPS = [
    { id: 'identity', label: 'Identity' },
    { id: 'capabilities', label: 'Capabilities' },
    { id: 'ops', label: 'Power & telemetry' },
    { id: 'variables', label: 'Variables' },
  ] as const;

  function patch<K extends keyof CatalogWritePayload>(key: K, value: CatalogWritePayload[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function toggleModule(module: WirelessModule, checked: boolean) {
    setForm((prev) => {
      const modules = checked
        ? prev.wireless.modules.includes(module)
          ? prev.wireless.modules
          : [...prev.wireless.modules, module]
        : prev.wireless.modules.filter((m) => m !== module);
      let protocols = [...prev.wireless.protocols];
      if (checked && MQTT_MODULES.has(module) && !protocols.includes('mqtt')) {
        protocols = [...protocols, 'mqtt'];
      }
      if (checked && module === 'lora' && !protocols.includes('lorawan')) {
        protocols = [...protocols, 'lorawan'];
      }
      return { ...prev, wireless: { modules, protocols } };
    });
  }

  function toggleProtocol(protocol: WirelessProtocol, checked: boolean) {
    setForm((prev) => {
      if (!checked) {
        const modules = new Set(prev.wireless.modules);
        if (protocol === 'mqtt' && [...MQTT_MODULES].some((m) => modules.has(m))) {
          setLocalError('MQTT is required when Wi‑Fi, BLE, or Cellular is selected');
          return prev;
        }
        if (protocol === 'lorawan' && modules.has('lora')) {
          setLocalError('LoRaWAN is required when LoRa is selected');
          return prev;
        }
      }
      setLocalError(null);
      const protocols = checked
        ? prev.wireless.protocols.includes(protocol)
          ? prev.wireless.protocols
          : [...prev.wireless.protocols, protocol]
        : prev.wireless.protocols.filter((p) => p !== protocol);
      return { ...prev, wireless: { ...prev.wireless, protocols } };
    });
  }

  function validateIdentity(): string | null {
    if (!form.type_name.trim()) return 'Type name is required';
    if (!form.hardware_version.trim()) return 'Hardware version is required';
    if (!form.category.trim()) return 'Category is required';
    return null;
  }

  function goNext() {
    setLocalError(null);
    if (step === 0) {
      const identityErr = validateIdentity();
      if (identityErr) {
        setLocalError(identityErr);
        return;
      }
    }
    if (step === 1) {
      const wirelessErr = validateWireless(form.wireless);
      if (wirelessErr) {
        setLocalError(wirelessErr);
        return;
      }
    }
    setStep((current) => Math.min(current + 1, CATALOG_STEPS.length - 1));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setLocalError(null);
    const identityErr = validateIdentity();
    if (identityErr) {
      setLocalError(identityErr);
      setStep(0);
      return;
    }
    const wirelessErr = validateWireless(form.wireless);
    if (wirelessErr) {
      setLocalError(wirelessErr);
      setStep(1);
      return;
    }
    onSubmit(buildPayload(form, aliasesCsv, fwVersionsCsv, extTypesCsv, extVarsCsv));
  }

  function updateVariable(index: number, next: CatalogVariable) {
    setForm((prev) => {
      const variables = [...prev.variables];
      variables[index] = next;
      return { ...prev, variables };
    });
  }

  function removeVariable(index: number) {
    setForm((prev) => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index),
    }));
  }

  const showBatteryDetails =
    form.features.supports_battery || form.battery.power_source === 'battery';

  const lastStep = CATALOG_STEPS.length - 1;

  return (
    <form className="login-form catalog-form" onSubmit={handleSubmit}>
      <FieldHelpGroup>
      <div className="catalog-form__steps" role="tablist" aria-label="Device type sections">
        {CATALOG_STEPS.map((item, index) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={step === index}
            className={`catalog-form__step${step === index ? ' catalog-form__step--active' : ''}`}
            onClick={() => {
              setLocalError(null);
              setStep(index);
            }}
          >
            <span className="catalog-form__step-index">{index + 1}</span>
            {item.label}
          </button>
        ))}
      </div>
      <section className="catalog-form__section" hidden={step !== 0}>
        <h3>Identity</h3>
        <FieldHelp label="Type name" help={catalogHelp.typeName}>
          <input
            value={form.type_name}
            onChange={(e) => patch('type_name', e.target.value)}
            placeholder={copyFrom ? 'Enter a new name for this copy' : undefined}
          />
        </FieldHelp>
        {copyFrom ? (
          <p className="hint-text">Enter a new name for this copy. Type ID is assigned automatically on create.</p>
        ) : mode === 'create' ? (
          <p className="hint-text">Type ID is assigned automatically on create.</p>
        ) : (
          <label>
            <span>Type ID</span>
            <input value={form.type_id ?? ''} disabled />
          </label>
        )}
        <FieldHelp label="Type aliases (comma-separated)" help={catalogHelp.typeAliases}>
          <input
            value={aliasesCsv}
            onChange={(e) => setAliasesCsv(e.target.value)}
            placeholder="Optional; leave empty if none"
          />
        </FieldHelp>
        <FieldHelp label="Description" help={catalogHelp.description}>
          <textarea
            value={form.description ?? ''}
            onChange={(e) => patch('description', e.target.value)}
            rows={2}
          />
        </FieldHelp>
        <div className="catalog-form__row">
          <FieldHelp label="Manufacturer" help={catalogHelp.manufacturer}>
            <input
              value={form.manufacturer ?? ''}
              onChange={(e) => patch('manufacturer', e.target.value)}
              list="catalog-manufacturers"
            />
            <datalist id="catalog-manufacturers">
              {manufacturers.map((manufacturer) => (
                <option key={manufacturer} value={manufacturer} />
              ))}
            </datalist>
          </FieldHelp>
          <FieldHelp label="Model" help={catalogHelp.model} className="catalog-form__field">
            <div className="catalog-form__control-with-action">
              {customModel || models.length === 0 ? (
                <input
                  value={form.model ?? ''}
                  onChange={(e) => patch('model', e.target.value)}
                  placeholder="Enter a new model name"
                />
              ) : (
                <select
                  value={form.model ?? ''}
                  onChange={(e) => patch('model', e.target.value)}
                >
                  <option value="">—</option>
                  {models.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                  {form.model && !models.includes(form.model) ? (
                    <option value={form.model}>{form.model}</option>
                  ) : null}
                </select>
              )}
              {models.length > 0 ? (
                <button
                  type="button"
                  className={
                    customModel
                      ? 'catalog-form__icon-button catalog-form__icon-button--shine'
                      : 'catalog-form__icon-button'
                  }
                  aria-label={customModel ? 'Use existing' : 'New model'}
                  title={customModel ? 'Use existing' : 'New model'}
                  onClick={() => {
                    if (customModel) {
                      setCustomModel(false);
                      const current = (form.model ?? '').trim();
                      patch('model', models.includes(current) ? current : '');
                    } else {
                      setCustomModel(true);
                      patch('model', '');
                    }
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 5v14M5 12h14"
                      stroke="currentColor"
                      strokeWidth="2.25"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              ) : null}
            </div>
          </FieldHelp>
        </div>
        <div className="catalog-form__row">
          <FieldHelp label="Hardware version" help={catalogHelp.hardwareVersion}>
            <input
              value={form.hardware_version}
              onChange={(e) => patch('hardware_version', e.target.value)}
            />
          </FieldHelp>
          <FieldHelp label="Category" help={catalogHelp.category} className="catalog-form__field">
            <div className="catalog-form__control-with-action">
              {customCategory || categories.length === 0 ? (
                <input
                  value={form.category}
                  onChange={(e) => patch('category', e.target.value)}
                  placeholder="Enter a new category"
                />
              ) : (
                <select
                  value={form.category}
                  onChange={(e) => patch('category', e.target.value)}
                >
                  <option value="">—</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                  {form.category && !categories.includes(form.category) ? (
                    <option value={form.category}>{form.category}</option>
                  ) : null}
                </select>
              )}
              {categories.length > 0 ? (
                <button
                  type="button"
                  className={
                    customCategory
                      ? 'catalog-form__icon-button catalog-form__icon-button--shine'
                      : 'catalog-form__icon-button'
                  }
                  aria-label={customCategory ? 'Use existing category' : 'New category'}
                  title={customCategory ? 'Use existing category' : 'New category'}
                  onClick={() => {
                    if (customCategory) {
                      setCustomCategory(false);
                      const current = form.category.trim();
                      patch('category', categories.includes(current) ? current : '');
                    } else {
                      setCustomCategory(true);
                      patch('category', '');
                    }
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 5v14M5 12h14"
                      stroke="currentColor"
                      strokeWidth="2.25"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              ) : null}
            </div>
          </FieldHelp>
          <FieldHelp label="Status" help={catalogHelp.status}>
            <select
              value={form.status}
              onChange={(e) => patch('status', e.target.value as CatalogStatus)}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FieldHelp>
        </div>
      </section>

      <section className="catalog-form__section" hidden={step !== 1}>
        <SectionHelp title="Wireless" help={catalogHelp.wirelessSection}>
          <p className="hint-text" style={{ margin: 0 }}>
            Modules
          </p>
          <div className="catalog-form__checks">
            {WIRELESS_MODULES.map((module) => (
              <label key={module} className="catalog-form__check">
                <input
                  type="checkbox"
                  checked={form.wireless.modules.includes(module)}
                  onChange={(e) => toggleModule(module, e.target.checked)}
                />
                <span>
                  {module === 'ble'
                    ? 'BLE'
                    : module === 'lora'
                      ? 'LoRa'
                      : module === 'wifi'
                        ? 'Wi‑Fi'
                        : 'Cellular'}
                </span>
              </label>
            ))}
          </div>
          <p className="hint-text" style={{ margin: '0.75rem 0 0' }}>
            Protocols
          </p>
          <div className="catalog-form__checks">
            {WIRELESS_PROTOCOLS.map((protocol) => (
              <label key={protocol} className="catalog-form__check">
                <input
                  type="checkbox"
                  checked={form.wireless.protocols.includes(protocol)}
                  onChange={(e) => toggleProtocol(protocol, e.target.checked)}
                />
                <span>{protocol === 'mqtt' ? 'MQTT' : 'LoRaWAN'}</span>
              </label>
            ))}
          </div>
        </SectionHelp>
      </section>

      <section className="catalog-form__section" hidden={step !== 2}>
        <h3>Firmware</h3>
        <FieldHelp label="Supported versions (comma-separated)" help={catalogHelp.supportedVersions}>
          <input value={fwVersionsCsv} onChange={(e) => setFwVersionsCsv(e.target.value)} />
        </FieldHelp>
        <div className="catalog-form__row">
          <FieldHelp label="Minimum version" help={catalogHelp.minimumVersion}>
            <input
              value={form.firmware?.minimum_version ?? ''}
              onChange={(e) =>
                patch('firmware', { ...form.firmware, minimum_version: e.target.value })
              }
            />
          </FieldHelp>
          <FieldHelp label="Latest version" help={catalogHelp.latestVersion}>
            <input
              value={form.firmware?.latest_version ?? ''}
              onChange={(e) =>
                patch('firmware', { ...form.firmware, latest_version: e.target.value })
              }
            />
          </FieldHelp>
          <FieldHelp label="Update channel" help={catalogHelp.updateChannel}>
            <select
              value={form.firmware?.update_channel ?? ''}
              onChange={(e) =>
                patch('firmware', {
                  ...form.firmware,
                  update_channel: (e.target.value || undefined) as FirmwareUpdateChannel | undefined,
                })
              }
            >
              <option value="">—</option>
              {UPDATE_CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </FieldHelp>
        </div>
      </section>

      <section className="catalog-form__section" hidden={step !== 1}>
        <SectionHelp title="Display" help={catalogHelp.displaySection}>
          <label className="catalog-form__check">
            <input
              type="checkbox"
              checked={form.display.has_display}
              onChange={(e) =>
                patch('display', { ...form.display, has_display: e.target.checked })
              }
            />
            <span>Has physical display</span>
          </label>
          {form.display.has_display ? (
            <>
              <FieldHelp label="Display reference" help={catalogHelp.displayReference}>
                <input
                  value={form.display.display_reference ?? ''}
                  onChange={(e) =>
                    patch('display', { ...form.display, display_reference: e.target.value })
                  }
                />
              </FieldHelp>
              <label className="catalog-form__check">
                <input
                  type="checkbox"
                  checked={form.display.has_color ?? false}
                  onChange={(e) =>
                    patch('display', { ...form.display, has_color: e.target.checked })
                  }
                />
                <span>Color display</span>
              </label>
            </>
          ) : null}
        </SectionHelp>
      </section>

      <section className="catalog-form__section" hidden={step !== 1}>
        <SectionHelp title="Features" help={catalogHelp.featuresSection}>
          <div className="catalog-form__checks">
            {(
              [
                ['allows_external_sensor', 'Allows external sensor'],
                ['supports_battery', 'Supports battery'],
                ['supports_alerts', 'Supports alerts'],
                ['supports_history', 'Supports history'],
                ['supports_location', 'Supports location'],
                ['supports_calibration', 'Supports calibration'],
                ['supports_fwota', 'Supports FW OTA'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="catalog-form__check">
                <input
                  type="checkbox"
                  checked={Boolean(form.features[key])}
                  onChange={(e) =>
                    patch('features', { ...form.features, [key]: e.target.checked })
                  }
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </SectionHelp>
      </section>

      <section className="catalog-form__section" hidden={step !== 2}>
        <h3>Battery / power</h3>
        <FieldHelp label="Power source" help={catalogHelp.powerSource}>
          <select
            value={form.battery.power_source}
            onChange={(e) =>
              patch('battery', {
                ...form.battery,
                power_source: e.target.value as BatteryPowerSource,
              })
            }
          >
            {POWER_SOURCES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </FieldHelp>
        {showBatteryDetails ? (
          <>
            <div className="catalog-form__row">
              <FieldHelp label="Battery variable key" help={catalogHelp.batteryVariableKey}>
                <input
                  value={form.battery.variable_key ?? ''}
                  onChange={(e) =>
                    patch('battery', { ...form.battery, variable_key: e.target.value })
                  }
                />
              </FieldHelp>
              <FieldHelp label="Unit" help={catalogHelp.batteryUnit}>
                <input
                  value={form.battery.unit ?? ''}
                  onChange={(e) => patch('battery', { ...form.battery, unit: e.target.value })}
                />
              </FieldHelp>
              <FieldHelp label="Reference" help={catalogHelp.batteryReference}>
                <input
                  value={form.battery.reference ?? ''}
                  onChange={(e) =>
                    patch('battery', { ...form.battery, reference: e.target.value })
                  }
                />
              </FieldHelp>
            </div>
            <div className="catalog-form__row">
              <FieldHelp label="Full value" help={catalogHelp.batteryFullValue}>
                <input
                  type="number"
                  value={form.battery.full_value ?? ''}
                  onChange={(e) =>
                    patch('battery', {
                      ...form.battery,
                      full_value: optionalNumber(e.target.value),
                    })
                  }
                />
              </FieldHelp>
              <FieldHelp label="Low threshold" help={catalogHelp.batteryLowThreshold}>
                <input
                  type="number"
                  value={form.battery.low_threshold ?? ''}
                  onChange={(e) =>
                    patch('battery', {
                      ...form.battery,
                      low_threshold: optionalNumber(e.target.value),
                    })
                  }
                />
              </FieldHelp>
              <FieldHelp label="Critical threshold" help={catalogHelp.batteryCriticalThreshold}>
                <input
                  type="number"
                  value={form.battery.critical_threshold ?? ''}
                  onChange={(e) =>
                    patch('battery', {
                      ...form.battery,
                      critical_threshold: optionalNumber(e.target.value),
                    })
                  }
                />
              </FieldHelp>
              <FieldHelp label="Expected lifetime (days)" help={catalogHelp.batteryLifetime}>
                <input
                  type="number"
                  value={form.battery.expected_lifetime_days ?? ''}
                  onChange={(e) =>
                    patch('battery', {
                      ...form.battery,
                      expected_lifetime_days: optionalNumber(e.target.value),
                    })
                  }
                />
              </FieldHelp>
            </div>
            <label className="catalog-form__check">
              <input
                type="checkbox"
                checked={form.battery.replaceable ?? false}
                onChange={(e) =>
                  patch('battery', { ...form.battery, replaceable: e.target.checked })
                }
              />
              <span>Replaceable battery</span>
            </label>
          </>
        ) : null}
      </section>

      {form.features.allows_external_sensor ? (
        <section className="catalog-form__section" hidden={step !== 2}>
          <h3>External sensor</h3>
          <FieldHelp label="Allowed types (comma-separated)" help={catalogHelp.externalAllowedTypes}>
            <input value={extTypesCsv} onChange={(e) => setExtTypesCsv(e.target.value)} />
          </FieldHelp>
          <FieldHelp label="Variable keys (comma-separated)" help={catalogHelp.externalVariableKeys}>
            <input value={extVarsCsv} onChange={(e) => setExtVarsCsv(e.target.value)} />
          </FieldHelp>
          <div className="catalog-form__row">
            <FieldHelp label="Max count" help={catalogHelp.externalMaxCount}>
              <input
                type="number"
                min={0}
                value={form.external_sensor?.max_count ?? ''}
                onChange={(e) =>
                  patch('external_sensor', {
                    ...form.external_sensor,
                    max_count: optionalNumber(e.target.value),
                  })
                }
              />
            </FieldHelp>
            <label className="catalog-form__check">
              <input
                type="checkbox"
                checked={form.external_sensor?.required ?? false}
                onChange={(e) =>
                  patch('external_sensor', {
                    ...form.external_sensor,
                    required: e.target.checked,
                  })
                }
              />
              <span>External sensor required</span>
            </label>
          </div>
        </section>
      ) : null}

      <section className="catalog-form__section" hidden={step !== 2}>
        <h3>Telemetry</h3>
        <div className="catalog-form__row">
          <FieldHelp label="Expected interval (seconds)" help={catalogHelp.expectedInterval}>
            <input
              type="number"
              min={1}
              value={form.telemetry.expected_interval_seconds}
              onChange={(e) =>
                patch('telemetry', {
                  ...form.telemetry,
                  expected_interval_seconds: Number(e.target.value) || 300,
                })
              }
              required
            />
          </FieldHelp>
          <FieldHelp label="Offline after (seconds)" help={catalogHelp.offlineAfter}>
            <input
              type="number"
              min={1}
              value={form.telemetry.offline_after_seconds}
              onChange={(e) =>
                patch('telemetry', {
                  ...form.telemetry,
                  offline_after_seconds: Number(e.target.value) || 900,
                })
              }
              required
            />
          </FieldHelp>
          <FieldHelp label="Timestamp unit" help={catalogHelp.timestampUnit}>
            <input value="epoch_ms" disabled />
          </FieldHelp>
        </div>
      </section>

      <section className="catalog-form__section" hidden={step !== 3}>
        <div className="catalog-form__section-head">
          <h3>Variables</h3>
          <button
            type="button"
            className="ghost-button"
            onClick={() => patch('variables', [...form.variables, emptyVariable()])}
          >
            Add variable
          </button>
        </div>
        {form.variables.length === 0 ? (
          <p className="hint-text">No variables yet. Optional for lab types; add when telemetry keys are known.</p>
        ) : null}
        {form.variables.map((variable, index) => (
          <div key={index} className="catalog-form__variable">
            <div className="catalog-form__section-head">
              <strong>Variable {index + 1}</strong>
              <button type="button" className="ghost-button" onClick={() => removeVariable(index)}>
                Remove
              </button>
            </div>
            <div className="catalog-form__row">
              <FieldHelp label="Key" help={catalogHelp.variableKey}>
                <input
                  value={variable.key}
                  onChange={(e) => updateVariable(index, { ...variable, key: e.target.value })}
                />
              </FieldHelp>
              <FieldHelp label="Label" help={catalogHelp.variableLabel}>
                <input
                  value={variable.label}
                  onChange={(e) => updateVariable(index, { ...variable, label: e.target.value })}
                />
              </FieldHelp>
              <FieldHelp label="Unit" help={catalogHelp.variableUnit}>
                <input
                  value={variable.unit}
                  onChange={(e) => updateVariable(index, { ...variable, unit: e.target.value })}
                />
              </FieldHelp>
            </div>
            <FieldHelp label="Ingestion keys (comma-separated)" help={catalogHelp.ingestionKeys}>
              <input
                value={joinCsv(variable.ingestion_keys)}
                onChange={(e) =>
                  updateVariable(index, {
                    ...variable,
                    ingestion_keys: splitCsv(e.target.value),
                  })
                }
              />
            </FieldHelp>
            <div className="catalog-form__row">
              <FieldHelp label="Source" help={catalogHelp.variableSource}>
                <select
                  value={variable.source}
                  onChange={(e) =>
                    updateVariable(index, {
                      ...variable,
                      source: e.target.value as VariableSource,
                    })
                  }
                >
                  {VARIABLE_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </FieldHelp>
              <FieldHelp label="Data type" help={catalogHelp.variableDataType}>
                <select
                  value={variable.data_type}
                  onChange={(e) =>
                    updateVariable(index, {
                      ...variable,
                      data_type: e.target.value as VariableDataType,
                    })
                  }
                >
                  {VARIABLE_DATA_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </FieldHelp>
              <FieldHelp label="Availability" help={catalogHelp.variableAvailability}>
                <select
                  value={variable.availability ?? ''}
                  onChange={(e) =>
                    updateVariable(index, {
                      ...variable,
                      availability: (e.target.value || undefined) as
                        | VariableAvailability
                        | undefined,
                    })
                  }
                >
                  {VARIABLE_AVAILABILITY.map((a) => (
                    <option key={a || 'none'} value={a}>
                      {a || '—'}
                    </option>
                  ))}
                </select>
              </FieldHelp>
              <label className="catalog-form__check">
                <input
                  type="checkbox"
                  checked={variable.required}
                  onChange={(e) =>
                    updateVariable(index, { ...variable, required: e.target.checked })
                  }
                />
                <span>Required</span>
              </label>
            </div>
            <div className="catalog-form__row">
              <FieldHelp label="Normal min" help={catalogHelp.normalMin}>
                <input
                  type="number"
                  value={variable.normal_range?.min ?? ''}
                  onChange={(e) =>
                    updateVariable(index, {
                      ...variable,
                      normal_range: {
                        min: optionalNumber(e.target.value) ?? 0,
                        max: variable.normal_range?.max ?? 0,
                      },
                    })
                  }
                />
              </FieldHelp>
              <FieldHelp label="Normal max" help={catalogHelp.normalMax}>
                <input
                  type="number"
                  value={variable.normal_range?.max ?? ''}
                  onChange={(e) =>
                    updateVariable(index, {
                      ...variable,
                      normal_range: {
                        min: variable.normal_range?.min ?? 0,
                        max: optionalNumber(e.target.value) ?? 0,
                      },
                    })
                  }
                />
              </FieldHelp>
            </div>
            <div className="catalog-form__row">
              <FieldHelp label="Warning low" help={catalogHelp.warningLow}>
                <input
                  type="number"
                  value={variable.thresholds?.warning_low ?? ''}
                  onChange={(e) =>
                    updateVariable(index, {
                      ...variable,
                      thresholds: {
                        warning_low: optionalNumber(e.target.value) ?? 0,
                        warning_high: variable.thresholds?.warning_high ?? 0,
                        critical_low: variable.thresholds?.critical_low ?? 0,
                        critical_high: variable.thresholds?.critical_high ?? 0,
                      },
                    })
                  }
                />
              </FieldHelp>
              <FieldHelp label="Warning high" help={catalogHelp.warningHigh}>
                <input
                  type="number"
                  value={variable.thresholds?.warning_high ?? ''}
                  onChange={(e) =>
                    updateVariable(index, {
                      ...variable,
                      thresholds: {
                        warning_low: variable.thresholds?.warning_low ?? 0,
                        warning_high: optionalNumber(e.target.value) ?? 0,
                        critical_low: variable.thresholds?.critical_low ?? 0,
                        critical_high: variable.thresholds?.critical_high ?? 0,
                      },
                    })
                  }
                />
              </FieldHelp>
              <FieldHelp label="Critical low" help={catalogHelp.criticalLow}>
                <input
                  type="number"
                  value={variable.thresholds?.critical_low ?? ''}
                  onChange={(e) =>
                    updateVariable(index, {
                      ...variable,
                      thresholds: {
                        warning_low: variable.thresholds?.warning_low ?? 0,
                        warning_high: variable.thresholds?.warning_high ?? 0,
                        critical_low: optionalNumber(e.target.value) ?? 0,
                        critical_high: variable.thresholds?.critical_high ?? 0,
                      },
                    })
                  }
                />
              </FieldHelp>
              <FieldHelp label="Critical high" help={catalogHelp.criticalHigh}>
                <input
                  type="number"
                  value={variable.thresholds?.critical_high ?? ''}
                  onChange={(e) =>
                    updateVariable(index, {
                      ...variable,
                      thresholds: {
                        warning_low: variable.thresholds?.warning_low ?? 0,
                        warning_high: variable.thresholds?.warning_high ?? 0,
                        critical_low: variable.thresholds?.critical_low ?? 0,
                        critical_high: optionalNumber(e.target.value) ?? 0,
                      },
                    })
                  }
                />
              </FieldHelp>
            </div>
          </div>
        ))}
      </section>

      {localError || error ? <div className="form-error">{localError || error}</div> : null}
      <div className="catalog-form__step-nav modal-card__actions">
        <button className="ghost-button" type="button" disabled={submitting} onClick={onCancel}>
          Cancel
        </button>
        {step > 0 ? (
          <button
            className="secondary-button"
            type="button"
            disabled={submitting}
            onClick={() => {
              setLocalError(null);
              setStep((current) => Math.max(0, current - 1));
            }}
          >
            Back
          </button>
        ) : null}
        {step < lastStep ? (
          <button className="primary-button" type="button" disabled={submitting} onClick={goNext}>
            Next
          </button>
        ) : (
          <button className="primary-button" type="submit" disabled={submitting}>
            {submitting
              ? mode === 'create'
                ? 'Creating…'
                : 'Saving…'
              : mode === 'create'
                ? 'Create type'
                : 'Save type'}
          </button>
        )}
      </div>
      </FieldHelpGroup>
    </form>
  );
}
